import React, {useEffect, useRef, useState, forwardRef} from 'react';
import {mergeRefs} from '@packages/systems/design-system/utils';
import {reportBug} from '@packages/utilities/reporter';
import useResizeObserver from '@packages/systems/ui/hooks/useResizeObserver';
import {useWebflowSiteModules} from './useWebflowSiteModules';

export const PLACEHOLDER_URL =
  'https://d3e54v103j8qbb.cloudfront.net/plugins/spline-placeholder.500eef70b9.splinecode';

export const isValidSplineUrl = (url) => /\.splinecode$/.test(url);

const getFrontendModule = (el, moduleName) => {
  const webflow = el.ownerDocument.defaultView.Webflow;
  if (!webflow || !webflow.require) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug(
      'GlobalWebflowObject',
      'Global webflow object is uninstantiated.',
      el.ownerDocument.defaultView
    );
  }
  return webflow.require(moduleName);
};

const usePrevious = (value, initial = null) => {
  const ref = useRef(initial);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const SplineElement = forwardRef(
  (
    {
      scene,
      container,
      previewMode,
      style,
      onMouseDown,
      onMouseUp,
      onMouseHover,
      onKeyDown,
      onKeyUp,
      onStart,
      onLookAt,
      onFollow,
      onWheel,
      onLoad,
      isEditingIx2,
      ...props
    },
    ref
  ) => {
    const canvasRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentUrl, setCurrentUrl] = useState('');
    const sceneUrl = isValidSplineUrl(scene) ? scene : PLACEHOLDER_URL;
    const wasEditingIx2 = usePrevious(isEditingIx2, false);

    const [reloadTime, setReloadTime] = useState(0);
    const lastReloadTime = usePrevious(reloadTime);
    useEffect(() => {
      if (!container) return;
      const handleSplineReload = () => setReloadTime(Date.now());
      container.addEventListener('RELOAD_SPLINE', handleSplineReload);
      return () => {
        container.removeEventListener('RELOAD_SPLINE', handleSplineReload);
      };
    }, [container]);

    // Force spline render when container is resized in design mode
    const resizeObserver = useResizeObserver(() => {
      if (!container) return;
      const frontend = getFrontendModule(container, 'spline');
      const moduleInstance = frontend?.getInstance(container);
      const spline = moduleInstance?.spline;
      if (spline && spline.isStopped) {
        spline._resize();
        spline.render();
      }
    });

    const webflowSiteModules = useWebflowSiteModules(container);

    // Initialize runtime when component is mounted
    useEffect(() => {
      setIsLoading(true);

      let moduleInstance;
      const events = [
        {name: 'mouseDown', cb: onMouseDown},
        {name: 'mouseUp', cb: onMouseUp},
        {name: 'mouseHover', cb: onMouseHover},
        {name: 'keyDown', cb: onKeyDown},
        {name: 'keyUp', cb: onKeyUp},
        {name: 'start', cb: onStart},
        {name: 'lookAt', cb: onLookAt},
        {name: 'follow', cb: onFollow},
        {name: 'scroll', cb: onWheel},
      ];

      const containerExists = container && canvasRef.current;
      const urlChanged = currentUrl !== sceneUrl;
      const ix2Changed = isEditingIx2 !== wasEditingIx2;
      const reloadTimeChangd = reloadTime !== lastReloadTime;
      if (
        containerExists &&
        (urlChanged || ix2Changed || reloadTimeChangd) &&
        webflowSiteModules
      ) {
        init();
      }

      async function init() {
        const frontend = getFrontendModule(container, 'spline');
        moduleInstance = await frontend?.createInstance(container, sceneUrl);

        if (!moduleInstance) {
          throw Error('Could not init spline scene', sceneUrl);
        }

        setCurrentUrl(sceneUrl);

        // Listen for any events passed to the react component
        for (const event of events) {
          if (event.cb) {
            moduleInstance.spline.addEventListener(event.name, event.cb);
          }
        }

        // Disable rendering and events outside of preview mode & ix2 timeline view
        const shouldDisable = !previewMode && !isEditingIx2;
        if (shouldDisable) {
          moduleInstance.spline.stop();
          resizeObserver.observe(container);
        }

        setIsLoading(false);
        onLoad?.(moduleInstance.spline);
      }

      return () => {
        if (!moduleInstance) return;

        for (const event of events) {
          if (event.cb) {
            moduleInstance.spline.removeEventListener(event.name, event.cb);
          }
        }
        moduleInstance.destroy();
      };
      // Note: it's important that this hook only updates on specific deps
      // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneUrl, previewMode, isEditingIx2, reloadTime, webflowSiteModules]);

    return (
      <canvas
        ref={mergeRefs([ref, canvasRef])}
        style={{
          display: isLoading ? 'none' : 'block',
          ...style,
        }}
        {...props}
      />
    );
  }
);

export default SplineElement;
